import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { apiService } from '../../service/Api';
import Drawer from '../common/Drawer';
import { Package, Plus, Edit2, Save, X, Loader2, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';

export default function ProductsSidePanel({ isOpen, onClose, onProductsUpdate }) {
  const { user } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editDescription, setEditDescription] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [creating, setCreating] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    domain: '',
    description: ''
  });

  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    if (isOpen) {
      loadProducts();
    }
  }, [isOpen]);

  const loadProducts = async () => {
    try {
      setLoading(true);
      const response = await apiService.getTailspinProducts();
      if (response.data.success) {
        setProducts(response.data.data || []);
      } else {
        toast.error('Failed to load products');
      }
    } catch (error) {
      console.error('Error loading products:', error);
      toast.error('Error loading products');
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (product) => {
    if (!isAdmin) return;
    setEditingId(product.id);
    setEditDescription(product.description || '');
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditDescription('');
  };

  const handleSaveEdit = async (productId) => {
    if (!isAdmin) return;
    
    try {
      const response = await apiService.updateTailspinProduct(productId, {
        description: editDescription
      });
      
      if (response.data.success) {
        toast.success('Product updated successfully');
        setEditingId(null);
        setEditDescription('');
        // Update local state optimistically
        setProducts(prevProducts => 
          prevProducts.map(p => 
            p.id === productId 
              ? { ...p, description: editDescription }
              : p
          )
        );
        // Update dashboard count without full reload
        if (onProductsUpdate) {
          onProductsUpdate();
        }
      } else {
        toast.error(response.data.message || 'Failed to update product');
        // Reload on error to get correct state
        await loadProducts();
      }
    } catch (error) {
      console.error('Error updating product:', error);
      toast.error('Error updating product');
      // Reload on error to get correct state
      await loadProducts();
    }
  };

  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    if (!isAdmin || creating) return;

    const { name, domain, description } = formData;
    if (!name || !domain) {
      toast.error('Name and domain are required');
      return;
    }

    setCreating(true);
    try {
      const response = await apiService.createTailspinProduct({
        name,
        domain,
        description: description || ''
      });

      if (response.data.success) {
        toast.success('Product created successfully');
        setFormData({ name: '', domain: '', description: '' });
        setShowCreateForm(false);
        // Reload products list in side panel
        await loadProducts();
        // Update dashboard count without full reload
        if (onProductsUpdate) {
          onProductsUpdate();
        }
      } else {
        toast.error(response.data.message || 'Failed to create product');
      }
    } catch (error) {
      console.error('Error creating product:', error);
      toast.error('Error creating product');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteClick = (productId) => {
    if (!isAdmin) return;
    setConfirmDeleteId(productId);
  };

  const handleCancelDelete = () => {
    setConfirmDeleteId(null);
  };

  const handleConfirmDelete = async (productId) => {
    if (!isAdmin) return;
    
    setDeletingId(productId);
    try {
      const response = await apiService.deleteTailspinProduct(productId);
      
      if (response.data.success) {
        toast.success('Product deleted successfully');
        setConfirmDeleteId(null);
        // Update local state optimistically
        setProducts(prevProducts => prevProducts.filter(p => p.id !== productId));
        // Update dashboard count without full reload
        if (onProductsUpdate) {
          onProductsUpdate();
        }
      } else {
        toast.error(response.data.message || 'Failed to delete product');
        // Reload on error to get correct state
        await loadProducts();
      }
    } catch (error) {
      console.error('Error deleting product:', error);
      toast.error('Error deleting product');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      title="Products"
      description="View and manage your products"
      size="lg"
    >
      <div className="flex flex-col h-full">
        {/* Header Actions */}
        {isAdmin && (
          <div className="mb-4">
            {!showCreateForm ? (
              <button
                onClick={() => setShowCreateForm(true)}
                className="flex items-center gap-2 px-4 py-2 rounded-lg transition-colors"
                style={{ 
                  backgroundColor: '#FF6B5A', 
                  color: 'white' 
                }}
                onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'}
                onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
              >
                <Plus className="w-4 h-4" />
                <span>Create Product</span>
              </button>
            ) : (
              <div className="border rounded-lg p-4" style={{ borderColor: '#D7B472', backgroundColor: '#F8F7F4' }}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold" style={{ color: '#0A0A10' }}>Create New Product</h3>
                  <button
                    onClick={() => {
                      setShowCreateForm(false);
                      setFormData({ name: '', domain: '', description: '' });
                    }}
                    className="p-1 rounded hover:bg-gray-200"
                  >
                    <X className="w-4 h-4" style={{ color: '#0A0A10' }} />
                  </button>
                </div>
                <form onSubmit={handleCreateSubmit} className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium mb-1" style={{ color: '#0A0A10' }}>
                      Name *
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg"
                      style={{ borderColor: '#D7B472', backgroundColor: 'white' }}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1" style={{ color: '#0A0A10' }}>
                      Domain *
                    </label>
                    <input
                      type="text"
                      value={formData.domain}
                      onChange={(e) => setFormData({ ...formData, domain: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg"
                      style={{ borderColor: '#D7B472', backgroundColor: 'white' }}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1" style={{ color: '#0A0A10' }}>
                      Description
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      rows={4}
                      className="w-full px-3 py-2 border rounded-lg"
                      style={{ borderColor: '#D7B472', backgroundColor: 'white' }}
                    />
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="submit"
                      disabled={creating}
                      className="flex items-center gap-2 px-4 py-2 rounded-lg transition-colors disabled:opacity-50"
                      style={{ backgroundColor: '#FF6B5A', color: 'white' }}
                    >
                      {creating ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          <span>Creating...</span>
                        </>
                      ) : (
                        <>
                          <Save className="w-4 h-4" />
                          <span>Create</span>
                        </>
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowCreateForm(false);
                        setFormData({ name: '', domain: '', description: '' });
                      }}
                      className="px-4 py-2 rounded-lg border transition-colors"
                      style={{ borderColor: '#D7B472', color: '#0A0A10' }}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            )}
          </div>
        )}

        {/* Products List */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin" style={{ color: '#FF6B5A' }} />
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-12">
              <Package className="w-12 h-12 mx-auto mb-3" style={{ color: '#0A0A10', opacity: 0.5 }} />
              <p style={{ color: '#0A0A10', opacity: 0.7 }}>No products yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {products.map((product) => (
                <div
                  key={product.id}
                  className="border rounded-lg p-4"
                  style={{ borderColor: '#D7B472', backgroundColor: '#F8F7F4' }}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <h3 className="font-semibold mb-1" style={{ color: '#0A0A10' }}>
                        {product.name}
                      </h3>
                      {product.domain && (
                        <p className="text-sm mb-2" style={{ color: '#0A0A10', opacity: 0.7 }}>
                          {product.domain}
                        </p>
                      )}
                    </div>
                    {isAdmin && editingId !== product.id && (
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleEditClick(product)}
                          className="p-1 rounded hover:bg-gray-200 transition-colors"
                          disabled={editingId === product.id || deletingId === product.id}
                          title="Edit description"
                        >
                          <Edit2 className="w-4 h-4" style={{ color: '#FF6B5A' }} />
                        </button>
                        <button
                          onClick={() => handleDeleteClick(product.id)}
                          className="p-1 rounded hover:bg-gray-200 transition-colors"
                          disabled={editingId === product.id || deletingId === product.id}
                          title="Delete product"
                        >
                          <Trash2 className="w-4 h-4" style={{ color: '#FF6B5A' }} />
                        </button>
                      </div>
                    )}
                  </div>

                  {editingId === product.id ? (
                    <div className="mt-3 space-y-2">
                      <textarea
                        value={editDescription}
                        onChange={(e) => setEditDescription(e.target.value)}
                        rows={4}
                        className="w-full px-3 py-2 border rounded-lg"
                        style={{ borderColor: '#D7B472', backgroundColor: 'white' }}
                        placeholder="Enter product description..."
                      />
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleSaveEdit(product.id)}
                          className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-colors"
                          style={{ backgroundColor: '#FF6B5A', color: 'white' }}
                        >
                          <Save className="w-3 h-3" />
                          <span>Save</span>
                        </button>
                        <button
                          onClick={handleCancelEdit}
                          className="px-3 py-1.5 rounded-lg text-sm border transition-colors"
                          style={{ borderColor: '#D7B472', color: '#0A0A10' }}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : confirmDeleteId === product.id ? (
                    <div className="mt-3 p-3 border rounded-lg" style={{ borderColor: '#FF6B5A', backgroundColor: 'rgba(255, 107, 90, 0.1)' }}>
                      <p className="text-sm mb-3" style={{ color: '#0A0A10' }}>
                        Are you sure you want to delete "{product.name}"? This action cannot be undone.
                      </p>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleConfirmDelete(product.id)}
                          disabled={deletingId === product.id}
                          className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-colors disabled:opacity-50"
                          style={{ backgroundColor: '#FF6B5A', color: 'white' }}
                        >
                          {deletingId === product.id ? (
                            <>
                              <Loader2 className="w-3 h-3 animate-spin" />
                              <span>Deleting...</span>
                            </>
                          ) : (
                            <>
                              <Trash2 className="w-3 h-3" />
                              <span>Delete</span>
                            </>
                          )}
                        </button>
                        <button
                          onClick={handleCancelDelete}
                          disabled={deletingId === product.id}
                          className="px-3 py-1.5 rounded-lg text-sm border transition-colors disabled:opacity-50"
                          style={{ borderColor: '#D7B472', color: '#0A0A10' }}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    product.description && (
                      <p className="text-sm mt-2" style={{ color: '#0A0A10', opacity: 0.7 }}>
                        {product.description}
                      </p>
                    )
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Drawer>
  );
}

